/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { FC } from "react";
import { Trans, useI18n } from "@bookingcom/lingojs-react";
import { Icon, Stack, Text, useViewport } from "@bookingcom/bui-react";
import { ClockIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import { UITimelineLeg } from "@flights/types";
import { AmenityProps, TimelineLegInfo } from "./TimelineLegInfo";
import getTechnicalStops from "../../../../utils/get-technical-stops";
import Frame from "../../Frame";
import styles from "./TimelineLeg.module.css";
import { useFormatDateTime } from "@bookingcom/flights-core";
import useUserAgent from "../../../../hooks/useUserAgent";
import { TimelineLeg as TimelineLegUI } from "@flights/search-ui";
import { trackWithDefaultStage } from "@flights/et-universal";

type Props = {
  leg: UITimelineLeg;
  idx: number;
  shouldHideAnythingBeneath?: boolean;
} & AmenityProps;

export const TimelineLeg: FC<Props> = ({
  leg,
  shouldHideAnythingBeneath,
  idx,
  showAmenities = false,
  amenitiesVariant,
  onAmenitiesPopoverOpen
}) => {
  const i18n = useI18n();
  const { isMobile } = useUserAgent();
  const { formats } = useFormatDateTime(i18n);
  const { isSmall } = useViewport();

  const { from, to } = leg;

  const departure = {
    date: formats.flightDateWeekday(from.departureTime || ""),
    time: formats.flightTime(from.departureTime || ""),
    iata: from.airportCode,
    name: from.airport,
    timestampAttributes: { "data-testid": "timeline_location_timestamp_departure" },
    airportAttributes: { "data-testid": "timeline_location_airport_departure" }
  };

  const arrival = {
    date: formats.flightDateWeekday(to.arrivalTime || ""),
    time: formats.flightTime(to.arrivalTime || ""),
    iata: to.airportCode,
    name: to.airport,
    timestampAttributes: { "data-testid": "timeline_location_timestamp_arrival" },
    airportAttributes: { "data-testid": "timeline_location_airport_arrival" }
  };

  const isResponsiveFixesSm = isSmall && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  const legInfo = (
    <TimelineLegInfo
      leg={leg}
      showAmenities={showAmenities}
      amenitiesVariant={amenitiesVariant}
      onAmenitiesPopoverOpen={onAmenitiesPopoverOpen}
    />
  );

  const technicalStops = getTechnicalStops.fromLeg(leg);
  let technicalStopsBlock = null;
  if (technicalStops.length > 0) {
    technicalStopsBlock = (
      <Stack
        gap={2}
        direction={isResponsiveFixesSm ? "column" : "row"}
        alignItems="center"
        mixin={{ paddingBlockStart: 8, paddingBlockEnd: 4, paddingInlineStart: 2 }}
        attributes={{ "data-testid": "timeline_leg_stop" }}
      >
        <Icon svg={ClockIcon} size="small" color="neutral_alt" />
        <Text>
          <Trans
            tag="flights_itinerary_technical_stop"
            variables={{ airport_code: technicalStops.map((stop) => stop.airportCode.code).join(",") }}
          />
        </Text>
      </Stack>
    );
  }

  let contentBetween, contentOnSide;
  if (isMobile) {
    contentBetween = (
      <Frame pt={4}>
        {legInfo}
        {technicalStopsBlock}
      </Frame>
    );
    contentOnSide = null;
  } else {
    contentBetween = technicalStopsBlock;
    contentOnSide = legInfo;
  }

  return (
    <TimelineLegUI
      departure={departure}
      arrival={arrival}
      contentBetweenDepartureAndArrival={contentBetween}
      contentOnSide={contentOnSide}
      className={shouldHideAnythingBeneath ? styles.hideAnythingBeneath : undefined}
      attributes={{ "data-testid": `timeline_leg_${idx}` }}
      isResponsiveFixesSm={isResponsiveFixesSm}
    />
  );
};
